import { useState } from 'react'
import type { CSSProperties, JSX, ReactNode } from 'react'

import clsx from 'clsx'

import {
  InfoIcon,
  Popover,
  Switch,
  Tooltip,
  useResizeObserver,
} from '@buffer-mono/popcorn'

import styles from './PublicPreviewBar.module.css'

export type PublicPreviewBarProps = {
  on: boolean
  onChange: (on: boolean) => void
  offLabel: ReactNode
  onLabel: ReactNode
  aboutLabel: string
  aboutTooltip: string
  about?: ReactNode
}

export function PublicPreviewBar({
  on,
  onChange,
  offLabel,
  onLabel,
  aboutLabel,
  aboutTooltip,
  about,
}: PublicPreviewBarProps): JSX.Element {
  const [barRef, { height }] = useResizeObserver<HTMLDivElement>({
    box: 'border-box',
  })
  return (
    <div
      className={styles.previewBarSlot}
      style={
        {
          '--preview-bar-height':
            height === undefined ? undefined : `${height}px`,
        } as CSSProperties
      }
    >
      <div
        ref={barRef}
        className={clsx(styles.previewBar, on && styles.previewBarOn)}
      >
        <span className={styles.previewStatus}>
          <span
            aria-hidden
            className={clsx(styles.statusDot, on && styles.statusDotOn)}
          />
          {on ? onLabel : offLabel}
          {about && (
            <PublicPreviewBarAbout label={aboutLabel} tooltip={aboutTooltip}>
              {about}
            </PublicPreviewBarAbout>
          )}
        </span>
        <span aria-hidden className={styles.previewDivider} />
        <Switch
          className={styles.previewSwitch}
          checked={on}
          onCheckedChange={onChange}
        >
          Preview mode
        </Switch>
      </div>
    </div>
  )
}

function PublicPreviewBarAbout({
  label,
  tooltip,
  children,
}: {
  label: string
  tooltip: string
  children: ReactNode
}): JSX.Element {
  const [open, setOpen] = useState(false)
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip content={tooltip} open={open ? false : undefined}>
        <Popover.Trigger>
          <button
            type="button"
            className={styles.aboutButton}
            aria-label={label}
          >
            <InfoIcon size="small" aria-hidden />
          </button>
        </Popover.Trigger>
      </Tooltip>
      <Popover.Content side="top" align="center" size="small">
        {children}
      </Popover.Content>
    </Popover>
  )
}
